import { useContext, useEffect, useMemo } from "react";
import { InteractionEvent, Role } from "@hulanbv/platformapp";
import { InteractionLoggerContext } from "./interaction-logger.hook";
import { useAuthContext } from "../authentication/authentication.context";

/**
 * Extracts the exerciseInviteId query parameter from the current URL and
 * registers the screen as an ACTIVITY interaction with that invite. Activity
 * screens open in a fresh browsing context (new tab / in-app browser) where
 * ExerciseScreen never mounts, so they must start their own interaction to
 * have their time logged.
 *
 * @returns the exerciseInviteId query parameter, or an empty string
 */
const useInviteInteractionFromQuery = (): string => {
  const { startInteraction } = useContext(InteractionLoggerContext);
  const { session } = useAuthContext();

  const exerciseInviteId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("exerciseInviteId") ?? "";
  }, []);

  useEffect(() => {
    // only invitees (clients) should generate interaction statistics;
    // practitioners previewing an activity must not pollute the data
    if (exerciseInviteId && session?.user?.role === Role.USER) {
      startInteraction(exerciseInviteId, InteractionEvent.ACTIVITY);
    }
  }, [exerciseInviteId, session?.user?.role, startInteraction]);

  return exerciseInviteId;
};

export { useInviteInteractionFromQuery };
